import axios from "axios";

const api = axios.create({
    baseURL: process.env.REACT_APP_API || "",
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

const unwrap = (response) => response.data;
const qs = (params = {}) => ({ params });

const inventoryApi = {
    meta: () => api.get("/inventory/meta").then(unwrap),

    dashboard: (params) =>
        api.get("/inventory/dashboard", qs(params)).then(unwrap),

    dashboardDateRange: () =>
        api.get("/inventory/dashboard/date-range").then(unwrap),

    locations: (params) =>
        api.get("/inventory/locations", qs(params)).then(unwrap),

    saveLocation: (id, payload) =>
        api
            .post(`/inventory/locations${id ? `/${id}` : ""}`, payload)
            .then(unwrap),

    deleteLocation: (id) =>
        api.delete(`/inventory/locations/${id}`).then(unwrap),

    categories: (params) =>
        api.get("/inventory/categories", qs(params)).then(unwrap),

    saveCategory: (id, payload) =>
        api
            .post(`/inventory/categories${id ? `/${id}` : ""}`, payload)
            .then(unwrap),

    brands: (params) =>
        api.get("/inventory/brands", qs(params)).then(unwrap),

    saveBrand: (id, payload) =>
        api
            .post(`/inventory/brands${id ? `/${id}` : ""}`, payload)
            .then(unwrap),

    deleteCategory: (id) =>
        api.delete(`/inventory/categories/${id}`).then(unwrap),

    deleteBrand: (id) =>
        api.delete(`/inventory/brands/${id}`).then(unwrap),

    models: (params) =>
        api.get("/inventory/models", qs(params)).then(unwrap),

    saveModel: (id, payload) =>
        api
            .post(`/inventory/models${id ? `/${id}` : ""}`, payload)
            .then(unwrap),

    deleteModel: (id) =>
        api.delete(`/inventory/models/${id}`).then(unwrap),

    initialEntries: (params) => api.get('/inventory/entries',qs(params)).then(unwrap),
    initialEntryDateRange: () => api.get('/inventory/entries/date-range').then(unwrap),
    products: (params) =>
        api.get("/inventory/products", qs(params)).then(unwrap),

    productDateRange: () =>
        api.get("/inventory/products/date-range").then(unwrap),

    purchaseInvoices: (params) => api.get("/inventory/purchase-invoices", qs(params)).then(unwrap),
    purchaseInvoiceDateRange: () => api.get("/inventory/purchase-invoices/date-range").then(unwrap),
    purchaseInvoice: (id, params={}) => api.get(`/inventory/purchase-invoices/${id}`, qs(params)).then(unwrap),
    confirmPurchaseInvoice: (id, payload) => api.post(`/inventory/purchase-invoices/${id}/confirm`, payload).then(unwrap),
    attachPurchaseInvoice: (id, formData) => api.post(`/inventory/purchase-invoices/${id}/attachment`, formData).then(unwrap),
    downloadPurchaseInvoiceAttachment: (id) => api.get(`/inventory/purchase-invoices/${id}/attachment`, { responseType: "blob" }),

    product: (id) =>
        api.get(`/inventory/products/${id}`).then(unwrap),

    validateImei: (imei, productId = null) =>
        api
            .post("/inventory/imei/validate", {
                imei,
                productId: productId || null,
            })
            .then(unwrap),

    generateSku: () =>
        api.get("/inventory/sku/generate").then(unwrap),

    validateSku: (sku, productId = null) =>
        api
            .post("/inventory/sku/validate", {
                sku,
                productId: productId || null,
            })
            .then(unwrap),

    validateProductModelTitle: (payload) =>
        api
            .post("/inventory/products/validate-model-title", payload)
            .then(unwrap),

    createProduct: (payload) =>
        api.post("/inventory/products", payload).then(unwrap),

    updateProduct: (id, payload) =>
        api.post(`/inventory/products/${id}`, payload).then(unwrap),

    deleteProduct: (id, payload = {}) =>
        api.delete(`/inventory/products/${id}`, { data: payload }).then(unwrap),

    reactivateSerial: (id, payload = {}) =>
        api.post(`/inventory/serials/${id}/reactivate`, payload).then(unwrap),

    previewProductImport: (rows) =>
        api.post("/inventory/products/import/preview", { rows }).then(unwrap),

    importProducts: (rows) =>
        api.post("/inventory/products/import", { rows }).then(unwrap),

    receipts: (params) =>
        api.get("/inventory/logistics-receipts", qs(params)).then(unwrap),

    receipt: (id) =>
        api.get(`/inventory/logistics-receipts/${id}`).then(unwrap),

    createReceipt: (formData) =>
        api.post("/inventory/logistics-receipts", formData).then(unwrap),

    confirmReceipt: (id, notes) =>
        api
            .post(`/inventory/logistics-receipts/${id}/confirm`, { notes })
            .then(unwrap),

    rejectReceipt: (id, reason) =>
        api
            .post(`/inventory/logistics-receipts/${id}/reject`, { reason })
            .then(unwrap),

    stock: (params) =>
        api.get("/inventory/stock", qs(params)).then(unwrap),

    initialEntry: (payload) =>
        api.post("/inventory/stock/initial-entry", payload).then(unwrap),

    exportTransferDetails: (params = {}) =>
        api.get("/inventory/transfers/export", qs(params)).then(unwrap),

    transfers: (params = {}) =>
        api.get("/inventory/transfers", qs(params)).then(unwrap),

    transferItems: (id) =>
        api.get(`/inventory/transfers/${id}/items`).then(unwrap),

    transferDateRange: () =>
        api.get("/inventory/transfers/date-range").then(unwrap),

    transferAvailability: (originLocationId, options = {}) =>
        api
            .get("/inventory/transfers/availability", {
                // _ts evita respuestas cacheadas sin agregar headers de petición
                // no simples. Esto mantiene el preflight CORS limitado a los
                // headers globales que ya usa la API (por ejemplo Authorization).
                params: {
                    originLocationId,
                    externalSale: options.externalSale ? 1 : 0,
                    _ts: Date.now(),
                },
            })
            .then(unwrap),

    createTransfer: (payload) =>
        api.post("/inventory/transfers", payload).then(unwrap),

    documentTransfer: (id, payload) =>
        api.post(`/inventory/transfers/${id}/documentation`, payload).then(unwrap),

    transitionTransfer: (id, action, payload = {}) =>
        api
            .post(`/inventory/transfers/${id}/${action}`, payload)
            .then(unwrap),

    shippingClients: (search = "", origin = "") =>
        api.get("/inventory/shipping-clients", { params: { search, origin } }).then(unwrap),

    createShippingClient: (payload) =>
        api.post("/inventory/shipping-clients", payload).then(unwrap),

    shippingUbigeos: (search = "") =>
        api.get("/inventory/shipping-ubigeos", { params: { search } }).then(unwrap),

    shippingGuideSedes: () =>
        api.get("/inventory/shipping-guides/sedes").then(unwrap),

    shippingGuides: (params = {}) =>
        api.get("/inventory/shipping-guides", qs(params)).then(unwrap),

    shippingGuideDateRange: () =>
        api.get("/inventory/shipping-guides/date-range").then(unwrap),

    shippingGuideContext: (transferId) =>
        api.get(`/inventory/shipping-guides/context/${transferId}`).then(unwrap),

    shippingGuide: (id) =>
        api.get(`/inventory/shipping-guides/${id}`).then(unwrap),

    createShippingGuide: (payload) =>
        api.post("/inventory/shipping-guides", payload).then(unwrap),

    preflightShippingGuide: (id) =>
        api.post(`/inventory/shipping-guides/${id}/preflight`).then(unwrap),

    sendShippingGuide: (id) =>
        api.post(`/inventory/shipping-guides/${id}/send`).then(unwrap),

    retryShippingGuide: (id) =>
        api.post(`/inventory/shipping-guides/${id}/retry`).then(unwrap),

    reissueShippingGuide: (id) =>
        api.post(`/inventory/shipping-guides/${id}/reissue`).then(unwrap),

    requestShippingGuideCancellation: (id, payload) =>
        api.post(`/inventory/shipping-guides/${id}/cancellation-request`, payload).then(unwrap),

    confirmShippingGuideCancellation: (id, payload) =>
        api.post(`/inventory/shipping-guides/${id}/cancellation-confirm`, payload).then(unwrap),

    reverseShippingGuideWithoutPhysicalMovement: (id, payload) =>
        api.post(`/inventory/shipping-guides/${id}/reverse-without-physical-movement`, payload).then(unwrap),

    shippingGuideValidation: (documentId) =>
        api.get(`/inventory/shipping-guide-documents/${documentId}/validation`).then(unwrap),

    greConfiguration: () =>
        api.get("/inventory/shipping-guides/config/rucs").then(unwrap),

    saveGreConfiguration: (rucId, payload) =>
        api.post(`/inventory/shipping-guides/config/rucs/${rucId}`, payload).then(unwrap),

    downloadShippingGuide: async (id, type, filename) => {
        const response = await api.get(`/inventory/shipping-guides/${id}/${type}`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    },

    downloadShippingGuideDocument: async (id, type, filename) => {
        const response = await api.get(`/inventory/shipping-guide-documents/${id}/${type}`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    },

    trace: (search) =>
        api.get("/inventory/traceability", qs({ search })).then(unwrap),

    saleReceipts: (params) =>
        api.get("/inventory/sale-receipts", qs(params)).then(unwrap),

    audit: (params) =>
        api.get("/inventory/audit", qs(params)).then(unwrap),
};

export default inventoryApi;
